/**
 * Admin "Clean Broken Videos" control.
 *
 * Three-step safe-by-construction flow for hard-deleting ghost
 * `generated_content` rows (rows whose mp4 is gone from disk AND not
 * in object storage AND has no external CDN URL):
 *
 *   1. User clicks "Check Broken Videos" → POST dry-run.
 *   2. Show the count.
 *   3. User clicks "Delete N Broken Videos" → confirmation modal →
 *      POST apply=true → toast + refresh.
 *
 * The backend safety filters are the actual guarantees. This
 * component just surfaces them.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Broom, Trash, Spinner, Info } from '@phosphor-icons/react';
import api from '@/utils/api';

const CleanBrokenVideosControl = () => {
  const [scanLoading, setScanLoading] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const runDryRun = async () => {
    if (scanLoading) return;
    setScanLoading(true);
    setApplyResult(null);
    try {
      const res = await api.post('/admin/ops/delete-ghost-rows?apply=false&limit=10000');
      setDryRunResult(res.data);
      const n = res.data?.would_delete_count ?? 0;
      if (n === 0) {
        toast.success('No broken videos found — your workspace is clean.');
      } else {
        toast.info(`${n} broken video row(s) found. Review before deleting.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setScanLoading(false);
    }
  };

  const runApply = async () => {
    if (applyLoading) return;
    setApplyLoading(true);
    try {
      const res = await api.post(
        '/admin/ops/delete-ghost-rows?apply=true&limit=10000'
      );
      setApplyResult(res.data);
      toast.success(
        `Cleaned up ${res.data?.would_delete_count ?? 0} broken video row(s).`
      );
      // Force a re-scan so the dry-run count drops to 0 and the UI reflects reality.
      setDryRunResult(null);
      setShowConfirm(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setApplyLoading(false);
    }
  };

  const wouldDeleteCount = dryRunResult?.would_delete_count ?? null;
  const keptRecoverable = dryRunResult?.kept_recoverable_count ?? 0;
  const keptExternal = dryRunResult?.kept_external_count ?? 0;

  return (
    <div className="mt-3 rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-slate-200 space-y-2">
      <div className="flex items-center gap-2">
        <Broom size={16} className="text-rose-300" weight="fill" />
        <span className="font-semibold text-rose-200">Clean Broken Videos</span>
        <span className="text-rose-300/70">— hard-delete ghost rows</span>
      </div>
      <p className="text-slate-400 leading-relaxed">
        Removes <strong>My Projects</strong> rows whose MP4 is missing
        from local disk AND not in persistent storage AND has no external
        CDN URL. Rows with a working video are <strong>never</strong>{' '}
        touched.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <button
          type="button"
          data-testid="clean-broken-videos-scan-btn"
          disabled={scanLoading || applyLoading}
          onClick={runDryRun}
          className="flex-1 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-medium flex items-center justify-center gap-1.5"
        >
          {scanLoading ? (
            <>
              <Spinner size={14} className="animate-spin" />
              <span>Scanning…</span>
            </>
          ) : (
            <span>{dryRunResult ? 'Re-scan' : 'Check Broken Videos'}</span>
          )}
        </button>
        <button
          type="button"
          data-testid="clean-broken-videos-apply-btn"
          disabled={
            applyLoading
            || scanLoading
            || wouldDeleteCount === null
            || wouldDeleteCount === 0
          }
          onClick={() => setShowConfirm(true)}
          className="flex-1 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-medium flex items-center justify-center gap-1.5"
        >
          <Trash size={14} />
          <span>
            {wouldDeleteCount !== null && wouldDeleteCount > 0
              ? `Delete ${wouldDeleteCount} Broken Video${wouldDeleteCount === 1 ? '' : 's'}`
              : 'Delete Broken Videos'}
          </span>
        </button>
      </div>

      {dryRunResult && (
        <div
          data-testid="clean-broken-videos-dry-run-result"
          className="mt-2 rounded border border-white/5 bg-black/20 p-2 text-[11px] space-y-1"
        >
          <p>
            Scanned: <strong>{dryRunResult.scanned}</strong>{' · '}
            Would delete:{' '}
            <strong className={wouldDeleteCount > 0 ? 'text-rose-300' : 'text-emerald-300'}>
              {wouldDeleteCount}
            </strong>
            {' · '}Kept (recoverable):{' '}
            <strong className="text-emerald-300">{keptRecoverable}</strong>
            {' · '}Kept (external CDN):{' '}
            <strong className="text-amber-300">{keptExternal}</strong>
          </p>
          {wouldDeleteCount === 0 && (
            <p className="text-emerald-300/80 flex items-center gap-1">
              <Info size={12} />
              <span>Nothing to clean. All projects are recoverable.</span>
            </p>
          )}
        </div>
      )}

      {applyResult && (
        <div
          data-testid="clean-broken-videos-apply-result"
          className="mt-2 rounded border border-emerald-500/30 bg-emerald-500/5 p-2 text-[11px] text-emerald-200"
        >
          ✓ Deleted {applyResult.would_delete_count} broken video row(s).
          Workspace cleaned.
        </div>
      )}

      {showConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="clean-broken-videos-confirm-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !applyLoading) {
              setShowConfirm(false);
            }
          }}
        >
          <div className="bg-slate-900 border border-rose-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center">
                <Trash size={20} className="text-rose-400" weight="fill" />
              </div>
              <h3 className="text-white font-bold text-lg">
                Hard-delete {wouldDeleteCount} broken row{wouldDeleteCount === 1 ? '' : 's'}?
              </h3>
            </div>
            <p className="text-slate-300 text-sm mb-2">
              You're about to permanently remove{' '}
              <span className="font-semibold text-white">
                {wouldDeleteCount} project row{wouldDeleteCount === 1 ? '' : 's'}
              </span>{' '}
              from <code>generated_content</code>.
            </p>
            <p className="text-slate-400 text-xs mb-5">
              Only rows with no recoverable asset (no objstore, no local file,
              no external CDN URL) are included.{' '}
              <strong className="text-emerald-300">
                {keptRecoverable} recoverable
              </strong>{' '}
              and{' '}
              <strong className="text-amber-300">
                {keptExternal} external-CDN
              </strong>{' '}
              row(s) will be preserved. This cannot be undone.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                data-testid="clean-broken-videos-confirm-cancel-btn"
                disabled={applyLoading}
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="clean-broken-videos-confirm-apply-btn"
                disabled={applyLoading}
                onClick={runApply}
                className="flex-1 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {applyLoading ? (
                  <>
                    <Spinner size={16} className="animate-spin" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <>
                    <Trash size={16} />
                    <span>Yes, delete {wouldDeleteCount}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CleanBrokenVideosControl;
